import { type FC, useMemo, useState } from "react";

import { useAppContext } from "@/shared/hooks/useAppContext";
import { useEstateItems } from "@/shared/hooks/useEstateItems";
import { useFormWithErrors } from "@/shared/hooks/useFormWithErrors";
import { uploadDocumentWithAuth } from "@/shared/utils/uploadSupportingDocument";
import { sentryUtils } from "@/shared/utils/sentry";
import { currencyInput } from "@/shared/utils/currencyInput";
import { FieldError } from "@/shared/components/ui/FieldError";
import { CurrencyInput } from "@/shared/components/ui/CurrencyInput";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { UploadZone } from "@/shared/components/ui/upload-zone";
import { AddItemLayout } from "@/pages/postloss/modules/components/AddItemLayout";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";

import { NONE_VALUE } from "./OtherDebtsChecklist";

interface DebtFieldConfig {
  title: string;
  counterpartyLabel: string;
  placeholder: string;
  itemType: string;
}

const DEBT_FIELD_CONFIG: Record<string, DebtFieldConfig> = {
  cosigned_loan: {
    title: "co-signed loan or lease",
    counterpartyLabel: "Lender or landlord",
    placeholder: "e.g., Wells Fargo, Property Management Co.",
    itemType: "Co-signed loan or lease",
  },
  unpaid_rent: {
    title: "unpaid rent",
    counterpartyLabel: "Landlord or property manager",
    placeholder: "e.g., Maple Creek Apartments",
    itemType: "Unpaid rent",
  },
  pending_lawsuits: {
    title: "pending lawsuits",
    counterpartyLabel: "Court or plaintiff",
    placeholder: "e.g., County Court, Jane Doe",
    itemType: "Pending lawsuits",
  },
  unfiled_tax_returns: {
    title: "unfiled tax returns",
    counterpartyLabel: "Tax authority",
    placeholder: "e.g., IRS, State Department of Revenue",
    itemType: "Unfiled tax returns",
  },
  tax_payment_plan: {
    title: "tax payment plan",
    counterpartyLabel: "Tax authority",
    placeholder: "e.g., IRS, State Department of Revenue",
    itemType: "Tax payment plan",
  },
  estate_inheritance_taxes: {
    title: "state estate or inheritance taxes",
    counterpartyLabel: "State tax authority",
    placeholder: "e.g., NY Department of Taxation",
    itemType: "State estate or inheritance taxes",
  },
};

interface DebtFormValues {
  counterparty: string;
  amount: string;
  documentFile: File | null;
}

const EMPTY_FORM: DebtFormValues = {
  counterparty: "",
  amount: "",
  documentFile: null,
};

interface AddOtherDebtProps extends FlowCustomComponentProps {
  debtKey: string;
}

export const AddOtherDebt: FC<AddOtherDebtProps> = ({ debtKey, goToScreen, answers }) => {
  const config = DEBT_FIELD_CONFIG[debtKey];
  const { person, user } = useAppContext();
  const estateId = person?.estate_id;
  const firstName = person?.first_name ?? "your loved one";
  const { addItem, isAddingItem } = useEstateItems(estateId);

  const { form, fieldErrors, setFieldErrors, updateForm } = useFormWithErrors<
    DebtFormValues,
    { counterparty?: string }
  >(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const amountInput = currencyInput(form.amount, (val) => updateForm({ ...form, amount: val }));

  const rawSelected = answers["other_debts_selected"];
  const selected: string[] = useMemo(() => {
    return Array.isArray(rawSelected) ? rawSelected.filter((v) => v !== NONE_VALUE) : [];
  }, [rawSelected]);

  const currentIndex = selected.indexOf(debtKey);
  const nextKey = currentIndex < selected.length - 1 ? selected[currentIndex + 1] : null;

  const advance = (updatedAnswers: Record<string, string | string[]>) => {
    if (nextKey) {
      goToScreen(`add_other_debt_${nextKey}`, updatedAnswers);
    } else {
      goToScreen("other_debts_checklist", updatedAnswers);
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting || isAddingItem || !estateId || !config) return;

    if (!form.counterparty.trim()) {
      setFieldErrors({
        counterparty: `Please enter the ${config.counterpartyLabel.toLowerCase()}`,
      });
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setFieldErrors({});

    try {
      let sourceDocumentId: number | undefined;
      if (form.documentFile) {
        sourceDocumentId = await uploadDocumentWithAuth(form.documentFile, estateId, "other");
      }

      await addItem({
        estate_id: estateId,
        category: "Unsecured Debts",
        classification: "debt",
        item_type: config.itemType,
        item_name: form.counterparty.trim(),
        balance: parseFloat(form.amount) || 0,
        source_document_id: sourceDocumentId,
      });

      advance({ ...answers, [`other_debt_${debtKey}_done`]: "yes" });
    } catch (err) {
      setError("Failed to add debt. Please try again.");
      sentryUtils.captureError(err, "AddOtherDebt submission failed", {
        userId: user?.user_id,
        estateId: person?.estate_id,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!config) return null;

  return (
    <div className="flex flex-col items-center">
      <h2 className="text-center">
        Add {firstName}&apos;s {config.title}
      </h2>
      <p className="mt-2 text-center text-secondary">
        Share what you know. We'll help you gather details later.
      </p>
      <div className="mt-8 w-full">
        <AddItemLayout
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting || isAddingItem}
          error={error}
          onSkip={() => advance({ ...answers, [`other_debt_${debtKey}_done`]: "skipped" })}
          skipLabel="Skip for now"
        >
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label required>{config.counterpartyLabel}</Label>
              <Input
                value={form.counterparty}
                onChange={(e) => updateForm({ ...form, counterparty: e.target.value })}
                placeholder={config.placeholder}
                className={fieldErrors.counterparty ? "border-error" : ""}
              />
              <FieldError message={fieldErrors.counterparty} />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>
                Amount owed <span className="text-tertiary">(optional)</span>
              </Label>
              <CurrencyInput
                value={amountInput.displayValue}
                onChange={amountInput.handleChange}
                placeholder="0.00"
              />
            </div>

            <UploadZone
              label="Related documents"
              labelSuffix="(optional)"
              file={form.documentFile}
              onFileSelect={(file) => updateForm({ ...form, documentFile: file })}
              onFileRemove={() => updateForm({ ...form, documentFile: null })}
            />
          </div>
        </AddItemLayout>
      </div>
    </div>
  );
};

export { DEBT_FIELD_CONFIG };
